import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const BASE_URL = process.env.REACT_APP_BASE_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include',
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    window.location.href = `${BASE_URL || ''}/auth/login/`;
  }

  return result;
};

export const ADapi = createApi({
  reducerPath: 'ADapi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'LocalDomain',
    'DomainController',
    'Agent',
    'Domain',
    'Collection',
    'Group',
    'UserWithProfile',
  ],
  endpoints: (build) => ({
    getAuthToken: build.query({
      query: () => ({
        url: '/api/token/by-session/',
        method: 'POST',
      }),
      transformResponse: (response) => {
        const { plan_features } = JSON.parse(
          atob(response.access.split('.')[1])
        );

        const {
          nets_agents,
          agents_active_directory,
          max_agent_domain_controllers,
          max_agent_local_records,
          max_desktop_agents,
        } = plan_features;

        const areLocalDomainsAvailable =
          max_agent_domain_controllers > 0 && max_agent_local_records > 0;
        const areAgentsAvailable = nets_agents > 0;
        const areCollectionsAvailable = agents_active_directory > 0;
        const maxDesktopAgent = max_desktop_agents > 0;

        return {
          areLocalDomainsAvailable,
          areAgentsAvailable,
          areCollectionsAvailable,
          maxDesktopAgent,
        };
      },
    }),
    getUser: build.query({
      query: () => ({
        url: '/auth/rest_api/v1/users/is_authenticate/',
      }),
    }),
    getMenu: build.query({
      query: (user_id) => `/content/rest_api/v1/users/${user_id}/cabinet_menu/`,
    }),
    getCollections: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/collections/`,
      providesTags: ['Collection'],
      transformResponse: (response) => response.results,
    }),
    createCollection: build.mutation({
      query: ({ user_id, payload }) => {
        return {
          url: `/ad/rest_api/v1/users/${user_id}/collections/`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ['Collection'],
    }),
    updateCollection: build.mutation({
      query: ({ user_id, id, collectionData }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/${id}/`,
        method: 'PATCH',
        body: collectionData,
      }),
      invalidatesTags: ['Collection'],
    }),
    deleteCollection: build.mutation({
      query: ({ user_id, id }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collection'],
      transformErrorResponse: (response) => response.data,
    }),
    updateCollectionPriority: build.mutation({
      query: ({ user_id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/priorities/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Collection'],
    }),
    getDomains: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/agent_domains/`,
      providesTags: ['Domain'],
      transformResponse: (response) => response.results,
    }),
    getGroups: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/ad_groups/`,
      transformResponse: (response) => response.results,
      providesTags: ['Group'],
    }),
    getProfiles: build.query({
      query: (user_id) => `/cabinet/rest_api/v1/users/${user_id}/profiles/`,
      providesTags: ['Profile'],
      transformResponse: (response) => response.results,
    }),
    getUsersWithProfiles: build.query({
      queryFn: async (user_id, api, extraOptions, baseQuery) => {
        let results = [];
        let nextLink = `/ad/rest_api/v1/users/${user_id}/ad_users_with_profiles/`;

        try {
          while (nextLink) {
            // Use baseQuery to perform the individual network request
            const result = await baseQuery(nextLink, api, extraOptions);

            if (result.error) {
              // Stop and return the error if any page request fails
              return { error: result.error };
            }

            const response = result.data;
            results = results.concat(response.results);
            nextLink = response.next_page_link;
          }

          // Return the combined array as the success result for this query
          return { data: results };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', data: error } };
        }
      },
      providesTags: ['UserWithProfile'],
    }),
    deleteUserWithProfile: build.mutation({
      query: ({ user_id, adUserId }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/ad_users/${adUserId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserWithProfile'],
    }),
    getDomainControllers: build.query({
      query: (user_id) =>
        `/agents/rest_api/v1/users/${user_id}/domain_controller/`,
      transformResponse: (response) => response.results,
      providesTags: (result = []) => [
        'DomainController',
        ...result.map(({ id }) => ({ type: 'DomainController', id })),
      ],
    }),
    createDomainController: build.mutation({
      query: ({ user_id, domainController }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/`,
        method: 'POST',
        body: domainController,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController', 'LocalDomain'],
    }),
    editDomainController: build.mutation({
      query: ({ user_id, domainController }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/${domainController.id}/`,
        method: 'PATCH',
        body: domainController,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController'],
    }),
    deleteDomainController: build.mutation({
      query: ({ user_id, domainControllerId }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/${domainControllerId}/`,
        method: 'DELETE',
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController', 'LocalDomain'],
    }),
    getLocalDomains: build.query({
      query: (user_id) => `/agents/rest_api/v1/users/${user_id}/local_domain/`,
      transformResponse: (response) => response.results,
      providesTags: (result = []) => [
        'LocalDomain',
        ...result.map(({ id }) => ({ type: 'LocalDomain', id })),
      ],
    }),
    createLocalDomain: build.mutation({
      query: ({ user_id, localDomain }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/`,
        method: 'POST',
        body: localDomain,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['LocalDomain'],
    }),
    editLocalDomain: build.mutation({
      query: ({ user_id, localDomain }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/${localDomain.id}/`,
        method: 'PATCH',
        body: localDomain,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['LocalDomain'],
    }),
    deleteLocalDomain: build.mutation({
      query: ({ user_id, id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LocalDomain'],
    }),
    createDomain: build.mutation({
      query: ({ user_id, body }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/agent_domains/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Domain'],
    }),
    deleteDomain: build.mutation({
      query: ({ user_id, id }) => {
        return {
          url: `/ad/rest_api/v1/users/${user_id}/agent_domains/${id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Domain'],
    }),
    updateDomain: build.mutation({
      query: ({ user_id, domainData, id, payload }) => {
        return {
          url: `/ad/rest_api/v1/users/${user_id}/agent_domains/${id}/`,
          method: 'PATCH',
          body: payload,
        };
      },
      invalidatesTags: ['Domain'],
    }),

    getAuthKey: build.query({
      query: (user_id) =>
        `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
      providesTags: ['AuthKey'],
      transformErrorResponse: (response) => response.status,
    }),
    createAuthKey: build.mutation({
      query: ({ user_id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
        method: 'POST',
      }),
      invalidatesTags: ['AuthKey', 'Agent'],
      transformErrorResponse: (response) => response.data,
    }),
    deleteAuthKey: build.mutation({
      query: ({ user_id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AuthKey', 'Agent'],
      transformErrorResponse: (response) => response.data,
    }),
    getAllAgents: build.query({
      // The argument will be the initial relative path: 'users/user_id/agentinfo/?page=1'
      queryFn: async (initialPath, api, extraOptions, baseQuery) => {
        let accumulatedResults = [];
        let nextLink = initialPath;

        try {
          while (nextLink) {
            // Use baseQuery to perform the individual network request
            const result = await baseQuery(nextLink, api, extraOptions);

            if (result.error) {
              // Stop and return the error if any page request fails
              return { error: result.error };
            }

            const response = result.data;
            accumulatedResults = accumulatedResults.concat(response.results);
            nextLink = response.next_page_link;
          }

          // Return the combined array as the success result for this query
          return { data: accumulatedResults };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', data: error } };
        }
      },
      providesTags: (result = []) => [
        'Agent',
        ...result.map(({ uid }) => {
          return { type: 'Agent', id: uid };
        }),
      ],
    }),
    getAgent: build.query({
      query: ({ user_id, uid }) =>
        `/agents/rest_api/v1/users/${user_id}/agentinfo/${uid}/`,
      providesTags: ['Agent'],
    }),
    updateAgent: build.mutation({
      query: ({ user_id, uid, updateAgentData }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agentinfo/${uid}/`,
        method: 'PATCH',
        body: updateAgentData,
      }),
      // TODO: Optimistic Update Logic
      // async onQueryStarted(
      //   { uid, updateAgentData },
      //   { dispatch, queryFulfilled }
      // ) {
      //   const queryArg = uid;

      //   // Manually update the cache for the 'getSingleItem' query using the exact argument (id)
      //   const patchResult = dispatch(
      //     ADapi.util.updateQueryData('getAgent', queryArg, (draft) => {
      //       // Apply the patch to the draft
      //       Object.assign(draft, updateAgentData);
      //     })
      //   );

      //   try {
      //     await queryFulfilled;
      //   } catch {
      //     // If the mutation fails, revert the optimistic update
      //     patchResult.undo();
      //   }
      // },
      invalidatesTags: ({ uid }) => [{ type: 'Agent', id: uid }],
    }),
    deleteAgent: build.mutation({
      query: ({ user_id, uid }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agentinfo/${uid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agent'],
    }),
    requestAgent: build.query({
      query: (user_id) => `/agents/rest_api/v1/users/${user_id}/build/`,
      providesTags: ['Agent'],
    }),
    requestBuildAgent: build.mutation({
      query: ({ user_id, pin }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/build/`,
        method: 'POST',
        body: { pin },
      }),
      invalidatesTags: ['Agent'],
      transformErrorResponse: (response) => response.data,
    }),
    getAgentsChangelog: build.query({
      query: () => '/agents/rest_api/v1/agents_changelog/',
      transformResponse: (response) => {
        // I get a linux data first, but it needs to be a windows data first
        return Object.entries(response || {}).toReversed();
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetAuthTokenQuery,
  useGetMenuQuery,
  useGetCollectionsQuery,
  useGetDomainsQuery,
  useGetGroupsQuery,
  useGetProfilesQuery,
  useGetUsersWithProfilesQuery,
  useDeleteUserWithProfileMutation,
  useGetDomainControllersQuery,
  useCreateDomainControllerMutation,
  useEditDomainControllerMutation,
  useDeleteDomainControllerMutation,
  useGetLocalDomainsQuery,
  useCreateLocalDomainMutation,
  useEditLocalDomainMutation,
  useCreateDomainMutation,
  useDeleteDomainMutation,
  useUpdateDomainMutation,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useUpdateCollectionPriorityMutation,
  useDeleteLocalDomainMutation,
  useGetAuthKeyQuery,
  useCreateAuthKeyMutation,
  useDeleteAuthKeyMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useGetAllAgentsQuery,
  useRequestAgentQuery,
  useRequestBuildAgentMutation,
  useGetAgentsChangelogQuery,
} = ADapi;
